import {
  ListCirclePicture,
  ListItemCirlcePicture,
} from "common/ListCirclePicture"
import { ShopList, ShopListItem, ShopTitle } from "common/ShopTypography"
import { Fragment, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useTypedSelector } from "store"
import styled from "styled-components"
import { selectedAccessoriesSelector } from "../accessories/accessoriesSelectors"
import { selectedColorSelector } from "../color/colorSelectors"
import EditDetailingSelection from "../summary/EditDetailingSelection"
import { selectedUpholsterySelector } from "../upholstery/upholsterySelectors"
import { Font, STEPS_EN } from "../wizard/constants"
import { selectedDetailingSelector } from "./detailingSelectors"

const StyledContainer = styled.div`
  font-size: 0.75rem;
  font-weight: bold;

  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 0.875rem;
    padding-bottom: ${(p) => p.theme.spacing(2)}px;
  }
`

const StyledMainDetails = styled.div<{ $hasBackground?: boolean }>`
  margin: 10px 0 20px 0;

  ${(p) =>
    p?.$hasBackground &&
    `
    background: ${p?.theme?.colors?.white};
    padding: 20px 30px;
  `}
`

const StyledShopTitle = styled(ShopTitle)`
  margin-bottom: 10px;
`

const StyledShopList = styled(ShopList)`
  margin: 0;
`

const StyledShopListItem = styled(ShopListItem)`
  display: block;
  margin: 10px 0;
  font-size: 16px;
  line-height: 20px;

  :first-child,
  :last-child {
    margin: 0;
  }
`

const ShopDetailList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 10px 0 23px 0;
`

const ShopDetailListItem = styled.li`
  font-family: ${Font.OENaPro};
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 0em;
  display: flex;
  align-items: center;
  gap: 8.3px;
  margin-bottom: 13px;

  :last-child {
    margin-bottom: 0;
  }
`

const ShopListItemMarker = styled.span`
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.darkGrey};
`

const ShopCategoryTitle = styled.h3`
  font-family: ${Font.OENaPro};
  font-size: 18px;
  font-weight: 300;
  line-height: 27px;
  letter-spacing: 0em;
  margin: 0;
  padding: 0;
`

type DetailingProps = {
  hasBackground?: boolean
}

const SelectedDetailingDetails = ({ hasBackground }: DetailingProps) => {
  const [isSummaryPage, setIsSummaryPage] = useState(false)
  const selectedDetailing = useTypedSelector(selectedDetailingSelector)
  const color = useTypedSelector(selectedColorSelector)
  const accessories = useTypedSelector(selectedAccessoriesSelector)
  const upholstery = useTypedSelector(selectedUpholsterySelector)
  const { pathname } = useLocation()

  useEffect(() => {
    if (window && window?.location)
      setIsSummaryPage(window?.location?.href.indexOf("summary") > 0)
  }, [])

  if (!selectedDetailing) return null

  const {
    details: { mainFeatures, categories, techinicalFeatures, name },
  } = selectedDetailing

  return (
    <StyledContainer>
      <ShopTitle tabIndex={0}>מאפיינים עיקריים</ShopTitle>
      <StyledMainDetails $hasBackground={hasBackground}>
        <ListCirclePicture>
          {pathname !== STEPS_EN.detailing && (
            <EditDetailingSelection
              detailTitle={`רמת גימור ${name}`}
              editUrl="/detailing"
            />
          )}
          {mainFeatures.map((feature, i) => {
            return (
              <ListItemCirlcePicture
                key={i}
                $key={`${feature?.name}_${i}`}
                content={feature?.name}
                image={feature?.image?.imageUrl}
              />
            )
          })}
          {isSummaryPage && (
            <>
              <EditDetailingSelection
                imageUrl={color?.details?.colorImage?.imageUrl || ""}
                detailTitle={color?.details?.colorImage?.alt ?? ""}
                editUrl="/color"
              />
              <EditDetailingSelection
                imageUrl={upholstery?.details?.thumbnailImage?.imageUrl}
                detailTitle={upholstery?.details?.name}
                editUrl="/upholstery"
              />

              {accessories.map((item, i) => (
                <EditDetailingSelection
                  key={i}
                  imageUrl={item?.details.image.imageUrl}
                  detailTitle={item?.details.name}
                  editUrl="/accessories"
                />
              ))}
            </>
          )}
        </ListCirclePicture>
      </StyledMainDetails>

      <StyledShopTitle tabIndex={0}>מאפיינים נוספים</StyledShopTitle>

      <StyledMainDetails $hasBackground={hasBackground}>
        {categories.map((category, i) => (
          <Fragment key={i}>
            <ShopCategoryTitle>{category?.name}</ShopCategoryTitle>
            <ShopDetailList>
              {category?.items.map((item, i) => (
                <ShopDetailListItem key={item.name + i}>
                  <ShopListItemMarker />
                  {item.name} {item.description}
                </ShopDetailListItem>
              ))}
            </ShopDetailList>
          </Fragment>
        ))}
      </StyledMainDetails>

      <StyledShopTitle tabIndex={0}>מפרט טכני</StyledShopTitle>
      <StyledMainDetails $hasBackground={hasBackground}>
        <StyledShopList $hideBullets={true}>
          {techinicalFeatures[0]?.items?.map((item, i) => (
            <StyledShopListItem key={i}>
              {item.name}: {item.description}
            </StyledShopListItem>
          ))}
        </StyledShopList>
      </StyledMainDetails>
    </StyledContainer>
  )
}

export default SelectedDetailingDetails
